import type { NextPage } from 'next';
import * as React from 'react';
import { ImSpoonKnife } from 'react-icons/im';
import { twMerge } from 'tailwind-merge';

import { useAccessibilitySettings } from '@/hooks/useAccessibilitySettings';

import Layout from '@/components/Layout';
import PageTitle from '@/components/sections/PageTitle';
import SEO from '@/components/utils/SEO';

const RequestRestaurant: NextPage = () => {
  const { useHighContrast } = useAccessibilitySettings();

  const mailtoLinkRef = React.useRef<HTMLAnchorElement>(null);

  const [restaurantName, setRestaurantName] = React.useState('');
  const [restaurantTown, setRestaurantTown] = React.useState('');

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    mailtoLinkRef.current?.click();
  };

  return (
    <Layout>
      <SEO title="Request Restaurant" description="Request Restaurant" />

      <PageTitle
        topComponent={
          <ImSpoonKnife
            className={twMerge(
              'h-20 w-20',
              useHighContrast ? 'text-black' : 'text-primary-600'
            )}
            aria-hidden="true"
          />
        }
        title="Request a restaurant"
      />

      <section
        className={twMerge(
          'py-10 lg:py-14',
          useHighContrast ? 'bg-contrast-yellow text-black' : 'bg-[#fbfbee]'
        )}
      >
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="p-6">
              <h2 className="text-2xl lg:text-3xl">
                {' '}
                Help us to grow the GoodFoodTalks network
              </h2>

              <p className="font-light mt-4 text-lg">
                Don’t see the restaurant you are looking for on the
                GoodFoodTalks site? Complete the form below and we will let them
                know that there is demand for them to sign up.
              </p>

              <form onSubmit={handleFormSubmit} className="space-y-6 mt-6">
                <label className="block">
                  <p className="mb-1 font-medium">Restaurant name</p>
                  <input
                    type="text"
                    id="restaurant-name"
                    name="restaurant-name"
                    placeholder="Type restaurant name"
                    aria-label="Restaurant name"
                    className={twMerge(
                      'rounded px-6 h-[50px] w-full',
                      useHighContrast
                        ? 'bg-contrast-yellow text-black border border-black placeholder:text-black'
                        : 'border border-gray-300'
                    )}
                    value={restaurantName}
                    onChange={e => setRestaurantName(e.target.value)}
                    required
                  />
                </label>

                <label className="block">
                  <p className="mb-1 font-medium">Restaurant town/city</p>
                  <input
                    type="text"
                    id="restaurant-town"
                    name="restaurant-town"
                    placeholder="Type restaurant town/city"
                    aria-label="Restaurant town/city"
                    className={twMerge(
                      'rounded px-6 h-[50px] w-full',
                      useHighContrast
                        ? 'bg-contrast-yellow text-black border border-black placeholder:text-black'
                        : 'border border-gray-300'
                    )}
                    value={restaurantTown}
                    onChange={e => setRestaurantTown(e.target.value)}
                    required
                  />
                </label>

                <div className="sm:flex">
                  <button
                    type="submit"
                    className={twMerge(
                      'btn btn-xl',
                      useHighContrast ? 'btn-contrast-yellow' : 'btn-primary'
                    )}
                  >
                    Request restaurant
                  </button>

                  <a
                    ref={mailtoLinkRef}
                    href={`mailto:matt@goodfoodtalks.com?subject=Restaurant Request - ${restaurantName} (${restaurantTown})`}
                    className="hidden"
                  >
                    Request restaurant
                  </a>
                </div>
              </form>

              <p className="text-sm italic mt-14 opacity-80">
                * Sending the form requires that you have a default mail
                application set on your device. If submitting the form does not
                work for you for this reason, please submit an email manually
                regarding your request to{' '}
                <strong>matt@goodfoodtalks.com</strong>
              </p>
            </div>

            <div className="row-start-1 lg:row-start-auto">
              <div
                className={twMerge(
                  'p-6',
                  useHighContrast ? 'bg-inherit' : 'bg-[#f6f4df]'
                )}
              >
                <h2 className="text-2xl lg:text-3xl">
                  Vote for popular restaurants
                </h2>

                <p className="font-light mt-4 text-lg">
                  We hear there is a demand for some of the restaurants below.
                  Place your vote against one of the below and let the
                  restaurants know that there is a demand for them to sign up!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RequestRestaurant;
